// Per-clue TRACE for the cryptic game — one create-only Firestore doc per attempt, written
// as each clue finishes (robust to mid-session drop-out), mirroring the 24 PuzzleTrace but
// for cryptic. Shares the capture stream types; `domain: 'cryptic'` distinguishes it in the
// single `labsTraces` collection (Firestore is schemaless; BQ `traces_raw_latest` filters on it).

import { TranscriptItem, InteractionEvent, CaptureMode } from '../../../shared/labs/types'
import { CaptureEnv } from '../../../shared/lib/env'

export type CrypticAnswerMode = 'typed' | 'voice-detected' | 'pass' | 'timeout' | null

export type CrypticTrace = {
  schemaVersion: number
  domain: 'cryptic' // discriminates cryptic traces from the 24 corpus in labsTraces
  // grouping / session
  sessionId: string
  mode: CaptureMode
  trialIndex: number
  sitting: number
  resumed: boolean
  aborted: boolean
  // puzzle identity + difficulty
  puzzleId: string // e.g. "cc_017"
  clue: string
  enumeration: string
  answer: string // canonical answer (for offline scoring; the player never sees it pre-commit)
  publisher: string
  llmSolveRate: number // mean LLM correctness (0..1) — difficulty axis + human-vs-LLM anchor
  band: number | null // test-mode difficulty band (0 hardest … n-1 easiest); null in free play
  // the trace itself
  transcript: TranscriptItem[]
  events: InteractionEvent[]
  committedTranscript: string
  sttEngine: 'webspeech' | 'deepgram' | 'google'
  // answer + correctness
  answerMode: CrypticAnswerMode
  typedAnswer: string | null
  matchedAnswer: string | null // which accepted answer the guess matched, if any
  correct: boolean
  correctnessReason: string
  // timing
  puzzleStartedAt: string // ISO wall clock
  puzzleDurationMs: number // load → commit
  reflectionDurationMs: number // commit → answer entry (covers the retro when one was shown)
  // instant-solve retrospective (aha payoff) — only populated when the solve was trivial
  // (fast / near-wordless; see trivialSolve.ts), which swaps the passive reflection for an active
  // untimed retro. The retro's spoken decode lives IN `transcript` in-stream (like the reflection),
  // bracketed by the retro_start/retro_end events; these fields carry what the transcript can't.
  trivialSolve: boolean
  solutionType: 'insight' | 'analytic' | null // self-reported: did it click, or was it worked out?
  // environment + consent, denormalised so each trace stands alone
  env: CaptureEnv
  consented: boolean
  source: { src: string | null; token: string | null }
  firstExposure: boolean
}

export const CRYPTIC_TRACE_SCHEMA_VERSION = 2 // v2: instant-solve retro fields
